import React, { useRef, useState } from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity } from 'react-native';
import { useRouter } from "expo-router";
import { Avatar } from "stream-chat-react";
import FontAwesome from '@expo/vector-icons/FontAwesome';
import * as WebBrowser from 'expo-web-browser';
import { useChannelData } from "@/hooks/useChannelData";
import { getStatusStyles } from "@/utils/statusStyles";
import { UniverseStreamChannel } from '@/services/stream/types';
import { Colors } from '@/constants/theme';
import { TransferChatModal } from './TransferChatModal';
import { PhonePickerModal } from './PhonePickerModal';
import { ConfirmModal } from './ConfirmModal.web';
import type { AvailablePhone, CallChannel } from '@/services/calls/types';
import {
    fetchAvailablePhones,
    sendCallRequest,
    CALL_REQUEST_FAILED_MESSAGE,
    CALL_REQUEST_SENT_MESSAGE,
    PHONES_LOAD_FAILED_MESSAGE,
} from '@/services/calls/client';
import { endChannel, transferChannel } from '@/services/universe/client';
import { getStatusPermissions } from '@/utils/statusPermissions';

interface ChannelHeaderProps {
    channel?: UniverseStreamChannel;
    isTablet?: boolean;
    onTransferChat?: () => void;
    onEndChat?: () => void;
}

export function ChannelHeader({ channel, isTablet, onTransferChat, onEndChat }: ChannelHeaderProps) {
    const { name, image, clientPhone, clientUrl, status, canCallWhatsApp, callStatus, callIsPermanent, callExpiresAt } = useChannelData(channel);
    const router = useRouter();
    const [transferVisible, setTransferVisible] = useState(false);
    const [requestingCall, setRequestingCall] = useState(false);
    const [endingChat, setEndingChat] = useState(false);
    const [endConfirmVisible, setEndConfirmVisible] = useState(false);
    // PSTN number picker: the list is fetched on "Phone" and the picker only
    // opens when there is more than one number to choose from.
    const [loadingPhones, setLoadingPhones] = useState(false);
    const [availablePhones, setAvailablePhones] = useState<AvailablePhone[]>([]);
    const [phonePickerVisible, setPhonePickerVisible] = useState(false);
    // Blocks double-clicks from opening the call screen/window twice.
    const startingCallRef = useRef(false);

    const statusConfig = getStatusStyles(status);
    const { canRequestWhatsAppCall, canTransferChat, canEndChat } = getStatusPermissions(status);

    const handleViewLeadProfile = () => {
        if (clientUrl) {
            WebBrowser.openBrowserAsync(clientUrl).catch(err => console.error("Couldn't load page", err));
        }
    };

    /**
     * Opens the call screen/window. `phone` is the PSTN number the guide picked;
     * when absent the backend dials the client's WhatsApp number (today's
     * behaviour), so the displayed `phone` stays `clientPhone`.
     */
    const startCall = (callChannel: CallChannel, phone?: AvailablePhone) => {
        if (startingCallRef.current) return;
        const channelId = channel?.id;
        if (!channelId) {
            console.warn('Cannot start call: channel has no id');
            return;
        }
        // All-string params: they travel as route params in-app and through
        // `window.desktop.openCall` (a `Record<string, string>`) on desktop.
        const params: Record<string, string> = {
            channel: callChannel,
            channel_id: channelId,
            name,
            phone: phone?.phone || clientPhone,
            image: image ?? '',
        };
        // Only a real `phones.id` on a PSTN call — a Lead's entry has `id: null`
        // and sending it back is a 422; `whatsapp` ignores it anyway.
        if (callChannel === 'pstn' && typeof phone?.id === 'number') {
            params.phone_id = String(phone.id);
        }
        startingCallRef.current = true;
        setTimeout(() => { startingCallRef.current = false; }, 1500);
        // In the Electron desktop shell, open the call in its own window. In a
        // plain browser (or an older shell without openCall), navigate in-app.
        if (window.desktop?.openCall) {
            window.desktop.openCall(params);
        } else {
            router.push({ pathname: '/call', params });
        }
    };

    /**
     * "Phone": look up the numbers this client can be called on. More than one
     * → let the guide pick; one or none → call straight through with no
     * `phone_id`, exactly as before.
     */
    const handlePhoneCall = async () => {
        if (loadingPhones || startingCallRef.current) return;
        const channelId = channel?.id;
        if (!channelId) {
            console.warn('Cannot start call: channel has no id');
            return;
        }
        setLoadingPhones(true);
        try {
            const phones = await fetchAvailablePhones(channelId);
            if (phones.length > 1) {
                setAvailablePhones(phones);
                setPhonePickerVisible(true);
            } else {
                startCall('pstn');
            }
        } catch (err: any) {
            console.error('Failed to load available phones', err);
            // Backend wording (403 "You do not have permission to access the
            // phones.", 404 "Conversation not found for this channel.") as-is.
            window.alert(err?.message || PHONES_LOAD_FAILED_MESSAGE);
        } finally {
            setLoadingPhones(false);
        }
    };

    const handlePhonePicked = (phone: AvailablePhone) => {
        setPhonePickerVisible(false);
        startCall('pstn', phone);
    };

    const handleRequestWhatsAppCall = async () => {
        const channelId = channel?.id;
        if (!channelId || requestingCall) {
            return;
        }
        setRequestingCall(true);
        try {
            const message = await sendCallRequest(channelId);
            window.alert(message || CALL_REQUEST_SENT_MESSAGE);
        } catch (err: any) {
            console.error('Failed to send WhatsApp call request', err);
            // The backend also refuses when permission is already granted, so
            // this reads as the backend's own wording, not an app error.
            window.alert(err?.message || CALL_REQUEST_FAILED_MESSAGE);
        } finally {
            setRequestingCall(false);
        }
    };

    const handleTransferChat = () => {
        setTransferVisible(true);
    };

    const handleTransfer = async (agentId: number) => {
        const channelId = channel?.id;
        if (!channelId) return;
        const result = await transferChannel(channelId, agentId);
        if (!result?.success) {
            const message = result?.message || 'Could not transfer the chat. Please try again.';
            window.alert(message);
            throw new Error(message);
        }
        window.alert(result.message);
        onTransferChat?.();
    };

    const handleEndChat = () => {
        if (!channel?.id || endingChat) return;
        setEndConfirmVisible(true);
    };

    const confirmEndChat = async () => {
        const channelId = channel?.id;
        if (!channelId || endingChat) return;
        setEndingChat(true);
        try {
            const result = await endChannel(channelId);
            if (!result?.success) {
                // Keep the modal open so the agent can retry.
                window.alert(result?.message || 'Could not end the chat. Please try again.');
                return;
            }
            setEndConfirmVisible(false);
            onEndChat?.();
        } finally {
            setEndingChat(false);
        }
    };

    // Compact badge on the approved WhatsApp button: permanence or expiry date.
    const whatsAppCallBadge = callIsPermanent
        ? 'Permanent'
        : callExpiresAt != null
            ? new Date(callExpiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
            : null;

    return (
        <View style={styles.container}>
            {/* Hover-tooltip styling shared by the ghosted (status-gated) chips. */}
            <style>
                {`
                .ghost-anchor {
                    position: relative;
                    display: flex;
                }
                .ghost-tooltip {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 50%;
                    transform: translateX(-50%) translateY(-4px);
                    background: #1F2937;
                    color: #FFF;
                    font-size: 12px;
                    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                    padding: 6px 10px;
                    border-radius: 6px;
                    white-space: nowrap;
                    opacity: 0;
                    pointer-events: none;
                    transition: opacity 0.15s ease, transform 0.15s ease;
                    z-index: 100;
                }
                .ghost-tooltip::before {
                    content: '';
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 5px solid transparent;
                    border-bottom-color: #1F2937;
                }
                .ghost-anchor:hover .ghost-tooltip {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
                `}
            </style>
            <View style={styles.customHeader}>
                <View style={styles.headerContent}>
                    {!isTablet && (
                        <TouchableOpacity
                            onPress={() => router.back()}
                            style={styles.headerLeftButton}
                            activeOpacity={0.7}
                        >
                            <FontAwesome name="angle-left" size={24} color="black" />
                        </TouchableOpacity>
                    )}

                    <View style={[styles.headerTitleContainer, isTablet && { marginLeft: 16 }]}>
                        <Avatar name={name} image={image} size={45} />
                        <View style={styles.headerTextContainer}>
                            <Text style={styles.headerTitleText} numberOfLines={1}>
                                {name}
                            </Text>
                            <View style={styles.subtitleRow}>
                                <Text style={styles.headerSubtitleText} numberOfLines={1}>
                                    {clientPhone}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.inlineActions}>
                        <TouchableOpacity style={styles.inlineButton} activeOpacity={0.7} onPress={handleViewLeadProfile}>
                            <FontAwesome name="external-link-square" size={14} color={Colors.brand} />
                            <Text style={styles.inlineButtonText}>View Profile</Text>
                        </TouchableOpacity>
                        {canCallWhatsApp ? (
                            <TouchableOpacity style={styles.inlineButton} activeOpacity={0.7} onPress={() => startCall('whatsapp')}>
                                <FontAwesome name="whatsapp" size={14} color="#25D366" />
                                <Text style={styles.inlineButtonText}>WhatsApp</Text>
                                {whatsAppCallBadge && (
                                    <View style={styles.callBadge}>
                                        <Text style={styles.callBadgeText}>{whatsAppCallBadge}</Text>
                                    </View>
                                )}
                            </TouchableOpacity>
                        ) : callStatus === 'pending' ? (
                            <View style={[styles.inlineButton, styles.inlineButtonPending]}>
                                <FontAwesome name="whatsapp" size={14} color="#F5A623" />
                                <Text style={[styles.inlineButtonText, styles.inlineButtonTextPending]}>
                                    Waiting for approval
                                </Text>
                            </View>
                        ) : canRequestWhatsAppCall ? (
                            <TouchableOpacity
                                style={[styles.inlineButton, styles.inlineButtonWhatsApp]}
                                activeOpacity={0.7}
                                onPress={handleRequestWhatsAppCall}
                                disabled={requestingCall}
                            >
                                <FontAwesome name="whatsapp" size={14} color="#25D366" />
                                <Text style={[styles.inlineButtonText, styles.inlineButtonTextWhatsApp]}>
                                    {requestingCall ? 'Sending…' : 'Send Request WA Call'}
                                </Text>
                            </TouchableOpacity>
                        ) : (
                            <div className="ghost-anchor">
                                <View style={[styles.inlineButton, styles.inlineButtonGhost]}>
                                    <FontAwesome name="whatsapp" size={14} color="#B9BDC1" />
                                    <Text style={[styles.inlineButtonText, styles.inlineButtonTextGhost]}>
                                        Send Request WA Call
                                    </Text>
                                </View>
                                <div className="ghost-tooltip">Available when the chat is active</div>
                            </div>
                        )}
                        <TouchableOpacity
                            style={[styles.inlineButton, loadingPhones && { opacity: 0.5 }]}
                            activeOpacity={0.7}
                            onPress={handlePhoneCall}
                            disabled={loadingPhones}
                        >
                            <FontAwesome name="phone" size={14} color={Colors.brand} />
                            <Text style={styles.inlineButtonText}>{loadingPhones ? 'Loading…' : 'UniPhone'}</Text>
                        </TouchableOpacity>
                        {canTransferChat && (
                            <TouchableOpacity style={styles.inlineButton} activeOpacity={0.7} onPress={handleTransferChat}>
                                <FontAwesome name="exchange" size={14} color={Colors.brand} />
                                <Text style={styles.inlineButtonText}>Transfer Chat</Text>
                            </TouchableOpacity>
                        )}
                        {canEndChat && (
                            <TouchableOpacity
                                style={[styles.inlineButton, styles.inlineButtonDanger, endingChat && { opacity: 0.5 }]}
                                activeOpacity={0.7}
                                onPress={handleEndChat}
                                disabled={endingChat}
                            >
                                <FontAwesome name="bell-slash" size={14} color="#E53935" />
                                <Text style={[styles.inlineButtonText, styles.inlineButtonTextDanger]}>
                                    {endingChat ? 'Ending…' : 'End Chat'}
                                </Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            </View>

            <View style={[styles.statusBar, { backgroundColor: statusConfig.backgroundColor }]}>
                <Text style={[styles.statusBarText, { color: statusConfig.textColor }]}>{statusConfig.text}</Text>
            </View>

            <TransferChatModal
                visible={transferVisible}
                channel={channel}
                onClose={() => setTransferVisible(false)}
                onTransfer={handleTransfer}
            />

            <PhonePickerModal
                visible={phonePickerVisible}
                phones={availablePhones}
                onClose={() => setPhonePickerVisible(false)}
                onSelect={handlePhonePicked}
            />

            <ConfirmModal
                visible={endConfirmVisible}
                title="End Chat"
                message={`Are you sure you want to end this chat with ${name}? The conversation will be marked as inactive.`}
                confirmLabel="End Chat"
                destructive
                loading={endingChat}
                onConfirm={confirmEndChat}
                onCancel={() => setEndConfirmVisible(false)}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#FFF',
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E5E5E5',
    },
    customHeader: {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        // Keep the header (and the WA-request tooltip inside it) above the
        // status bar rendered after it.
        zIndex: 2,
    },
    headerContent: {
        height: 60,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 0,
    },
    headerTitleContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerTextContainer: {
        marginLeft: 12,
        maxWidth: '70%',
    },
    headerTitleText: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#000',
    },
    subtitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 2,
    },
    headerSubtitleText: {
        fontSize: 14,
        color: '#666',
    },
    avatar: {
        width: 45,
        height: 45,
        borderRadius: 22.5,
    },
    avatarFallback: {
        width: 45,
        height: 45,
        borderRadius: 22.5,
        backgroundColor: '#CCC',
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarFallbackText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: '600',
    },
    headerLeftButton: {
        marginLeft: 0,
        paddingHorizontal: 16,
        paddingVertical: 8,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'transparent',
    },
    statusBar: {
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
    },
    statusBarText: {
        fontSize: 10,
        fontWeight: '600',
    },
    inlineActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginRight: 12,
    },
    inlineButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: Colors.brand,
    },
    inlineButtonText: {
        fontSize: 13,
        fontWeight: '500',
        color: Colors.brand,
    },
    callBadge: {
        backgroundColor: '#E7F8EE',
        borderRadius: 4,
        paddingHorizontal: 5,
        paddingVertical: 1,
        marginLeft: 2,
    },
    callBadgeText: {
        fontSize: 10,
        fontWeight: '600',
        color: '#1FA855',
    },
    inlineButtonDanger: {
        borderColor: '#E53935',
    },
    inlineButtonTextDanger: {
        color: '#E53935',
    },
    inlineButtonWhatsApp: {
        borderColor: '#25D366',
    },
    inlineButtonTextWhatsApp: {
        color: '#25D366',
    },
    inlineButtonPending: {
        borderColor: '#F5A623',
    },
    inlineButtonTextPending: {
        color: '#F5A623',
    },
    inlineButtonGhost: {
        borderColor: '#D6D9DD',
        opacity: 0.65,
    },
    inlineButtonTextGhost: {
        color: '#9CA3AF',
    },
});
